import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LogOut, Plus, Clock, CheckCircle, XCircle, TrendingUp, Loader2 } from "lucide-react";
import "../dashboard.css";

const VolunteerDashboard = ({ user, logout }) => {
  const [hours, setHours] = useState([]);
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingHour, setEditingHour] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showApprovedHours, setShowApprovedHours] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [approvedStats, setApprovedStats] = useState({
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: "09:00",
    hours: "",
    activity_type: "",
    description: ""
  });

  const activityTypes = ["coaching", "maintenance", "admin", "meeting", "community work", "other"];

  const activityDefaults = {
    coaching: { start_time: "19:00", hours: "2" },
    maintenance: { start_time: "10:00", hours: "2" },
    admin: { start_time: "18:00", hours: "2" },
    meeting: { start_time: "20:00", hours: "1" },
    "community work": { start_time: "10:00", hours: "3" },
    other: { start_time: "10:00", hours: "2" }
  };

  useEffect(() => {
    fetchHours();
    fetchStats();
  }, []);

  const fetchHours = async () => {
    try {
      const response = await axios.get(`${API}/hours`);
      const sortedHours = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setHours(sortedHours);
      calculateApprovedStats(sortedHours);
    } catch (error) {
      toast.error("Failed to fetch hours");
    }
  };

  const calculateApprovedStats = (allHours) => {
    const now = new Date();
    
    // Get Monday of current week at 00:01
    const currentDay = now.getDay();
    const diff = currentDay === 0 ? -6 : 1 - currentDay; // If Sunday, go back 6 days, else go back to Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 1, 0, 0);
    
    // Get first day of current month
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    // Get first day of current year
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
    
    // Only include club_approved (supervisor approved) hours
    // Filter by the date the hours actually occurred (h.date field)
    const approvedHours = allHours.filter(h => h.status === 'club_approved');
    
    const thisWeek = approvedHours
      .filter(h => new Date(h.date) >= monday)
      .reduce((sum, h) => sum + (h.hours || 0), 0);
    
    const thisMonth = approvedHours
      .filter(h => new Date(h.date) >= firstDayOfMonth)
      .reduce((sum, h) => sum + (h.hours || 0), 0);
    
    const thisYear = approvedHours
      .filter(h => new Date(h.date) >= firstDayOfYear)
      .reduce((sum, h) => sum + (h.hours || 0), 0);
    
    setApprovedStats({
      thisWeek: Math.round(thisWeek * 10) / 10,
      thisMonth: Math.round(thisMonth * 10) / 10,
      thisYear: Math.round(thisYear * 10) / 10
    });
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    const tenDaysAgo = new Date(today);
    tenDaysAgo.setDate(today.getDate() - 10);
    return tenDaysAgo.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.activity_type) {
      toast.error("Please select an activity type");
      return;
    }

    const selectedDate = new Date(formData.date);
    const minDate = new Date(getMinDate());
    const today = new Date();
    
    if (selectedDate < minDate) {
      toast.error("You can only log hours from the last 10 days");
      return;
    }
    
    if (selectedDate > today) {
      toast.error("You cannot log hours for future dates");
      return;
    }

    // Show confirmation modal instead of submitting directly
    setDialogOpen(false);
    setShowConfirmationModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmationModal(false);
    await submitHours();
  };

  const handleConfirmCancel = () => {
    setShowConfirmationModal(false);
    setDialogOpen(true);
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTimeForDisplay = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  const submitHours = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/hours`, {
        date: formData.date,
        start_time: formData.start_time,
        hours: parseFloat(formData.hours),
        activity_type: formData.activity_type,
        description: formData.description
      });
      toast.success("Hours logged successfully!");
      setDialogOpen(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        start_time: "09:00",
        hours: "",
        activity_type: "",
        description: ""
      });
      fetchHours();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log hours");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (hour) => {
    setEditingHour(hour);
    setFormData({
      date: hour.date,
      start_time: hour.start_time,
      hours: hour.hours.toString(),
      activity_type: hour.activity_type,
      description: hour.description
    });
    setShowEditDialog(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();

    if (!formData.activity_type) {
      toast.error("Please select an activity type");
      return;
    }

    await submitEditUpdate();
  };

  const submitEditUpdate = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/hours/${editingHour.id}/edit`, {
        date: formData.date,
        start_time: formData.start_time,
        hours: parseInt(formData.hours),
        activity_type: formData.activity_type,
        description: formData.description
      });
      toast.success("Hours updated successfully!");
      setShowEditDialog(false);
      setEditingHour(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        start_time: "09:00",
        hours: "",
        activity_type: "",
        description: ""
      });
      fetchHours();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update hours");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "club_approved" || status === "platform_approved" || status === "approved") {
      return <Badge className="badge-modern badge-success"><CheckCircle className="w-3 h-3" />Approved</Badge>;
    } else if (status === "rejected") {
      return <Badge className="badge-modern badge-danger"><XCircle className="w-3 h-3" />Rejected</Badge>;
    }
    return <Badge className="badge-modern badge-warning"><Clock className="w-3 h-3" />Pending</Badge>;
  };

  return (
    <div className="dashboard-container gradient-red">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg" 
                alt="Logo" 
                className="w-12 h-12 rounded-lg"
              />
              <div>
                <h1 className="text-2xl font-black text-red-600 brand-font leading-none">PlayBack Token</h1>
                <p className="text-sm font-medium text-gray-700 mt-0.5">{user.club_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-700">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user.role}</p>
              </div>
              <Button variant="outline" onClick={logout} data-testid="logout-button" className="border-red-300 text-red-600 hover:bg-red-50 btn-modern">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="space-y-6" style={{ maxWidth: '600px', margin: '0 auto', paddingTop: '4rem' }}>
          {/* Big Buttons */}
          <div className="space-y-4">
            <button
              onClick={() => setDialogOpen(true)}
              className="w-full"
              style={{
                padding: '2rem',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '1.5rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(220, 38, 38, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(220, 38, 38, 0.3)';
              }}
            >
              <Plus size={32} />
              Log Volunteer Hours
            </button>

            <button
              onClick={() => setShowApprovedHours(true)}
              className="w-full"
              style={{
                padding: '2rem',
                background: 'white',
                color: '#dc2626',
                border: '3px solid #dc2626',
                borderRadius: '16px',
                fontSize: '1.5rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fef2f2';
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              }}
            >
              <CheckCircle size={32} />
              View Approved Hours
            </button>
          </div>
        </div>
      </main>

      {/* Log Hours Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl text-red-600">Log Your Hours</DialogTitle>
                    <DialogDescription>Fill in the details of your volunteer work</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        data-testid="log-date-input"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        min={getMinDate()}
                        max={new Date().toISOString().split('T')[0]}
                        required
                        className="border-red-200"
                      />
                      <p className="text-xs text-gray-500">Last 10 days only</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="activity_type">Activity Type *</Label>
                      <Select 
                        value={formData.activity_type} 
                        onValueChange={(value) => {
                          const defaults = activityDefaults[value];
                          setFormData({ 
                            ...formData, 
                            activity_type: value,
                            start_time: defaults.start_time,
                            hours: defaults.hours
                          });
                        }} 
                        required
                      >
                        <SelectTrigger data-testid="log-activity-select" className="border-red-200">
                          <SelectValue placeholder="Select activity type" />
                        </SelectTrigger>
                        <SelectContent>
                          {activityTypes.map((type) => (
                            <SelectItem key={type} value={type} data-testid={`activity-${type}`}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="start_time">Start Time *</Label>
                        <Input
                          id="start_time"
                          data-testid="log-starttime-input"
                          type="time"
                          value={formData.start_time}
                          onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                          required
                          className="border-red-200"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="hours">Hours *</Label>
                        <Input
                          id="hours"
                          data-testid="log-hours-input"
                          type="number"
                          step="1"
                          min="1"
                          max="24"
                          value={formData.hours}
                          onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                          placeholder="e.g., 2"
                          required
                          className="border-red-200"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        data-testid="log-description-input"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="e.g., Coaching under 13 boys"
                        className="border-red-200 resize-none"
                        rows={3}
                        required
                      />
                    </div>

                    <Button type="submit" data-testid="log-submit-button" className="w-full btn-primary-modern btn-modern" disabled={loading}>
                      {loading ? "Submitting..." : "Submit Hours"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

      {/* Edit Hours Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">Edit Volunteer Hours</DialogTitle>
            <DialogDescription>Update your logged hours (pending approval only)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-date">Date *</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={getMinDate()}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className="border-red-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-start-time">Start Time *</Label>
                <Input
                  id="edit-start-time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  className="border-red-200"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-hours">Hours *</Label>
                <Input
                  id="edit-hours"
                  type="number"
                  min="1"
                  max="24"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                  placeholder="Enter whole hours"
                  required
                  className="border-red-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-activity">Activity Type *</Label>
                <Select 
                  value={formData.activity_type} 
                  onValueChange={(value) => {
                    const defaults = activityDefaults[value];
                    setFormData({ 
                      ...formData, 
                      activity_type: value,
                      start_time: defaults.start_time,
                      hours: defaults.hours
                    });
                  }}
                >
                  <SelectTrigger className="border-red-200">
                    <SelectValue placeholder="Select activity" />
                  </SelectTrigger>
                  <SelectContent>
                    {activityTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        <span className="capitalize">{type}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description *</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Coaching under 13 boys"
                className="border-red-200 resize-none"
                rows={3}
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 btn-primary-modern"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approved Hours Modal */}
      <Dialog open={showApprovedHours} onOpenChange={setShowApprovedHours}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">Supervisor Approved Hours</DialogTitle>
            <DialogDescription>Hours approved by your club supervisor (based on when hours occurred)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-800">This Week</span>
                <span className="text-2xl font-bold text-green-600">{approvedStats.thisWeek}h</span>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-800">This Month</span>
                <span className="text-2xl font-bold text-blue-600">{approvedStats.thisMonth}h</span>
              </div>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-800">This Year</span>
                <span className="text-2xl font-bold text-purple-600">{approvedStats.thisYear}h</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button 
              onClick={() => setShowApprovedHours(false)}
              className="btn-primary-modern"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      {showConfirmationModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ zIndex: 99999 }}
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Your Hours</h3>
              <p className="text-gray-600 text-sm">Please review the details before submitting</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Date</span>
                <span className="font-semibold text-gray-800">{formatDateForDisplay(formData.date)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Activity Type</span>
                <span className="font-semibold text-gray-800 capitalize">{formData.activity_type}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Start Time</span>
                <span className="font-semibold text-gray-800">{formatTimeForDisplay(formData.start_time)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Hours</span>
                <span className="font-bold text-red-600 text-lg">{formData.hours}h</span>
              </div>
              <div className="pt-2">
                <span className="text-sm text-gray-600 block mb-1">Description</span>
                <p className="text-gray-800 bg-white p-2 rounded border border-gray-200 text-sm">{formData.description}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleConfirmCancel}
                data-testid="confirm-cancel-btn"
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={loading}
                data-testid="confirm-submit-btn"
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Confirm & Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default VolunteerDashboard;
